// helpers/fileType.js
export const officeFormats = [
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "odt",
  "rtf",
];

export const formatsAviable = {
  images: [
    "jpeg",
    "jpg",
    "png",
    "webp",
    "gif",
    "bmp",
    "tiff",
    "svg",
    "ico",
    "heic",
  ],
  documents: [
    "pdf",
    "txt",
    "csv",
    "vnd.openxmlformats-officedocument.spreadsheetml.sheet", // mime-like entry
    ...officeFormats,
  ],
  audios: ["mp3", "wav", "flac", "aac", "m4a", "wma", "ogg", "alac", "mpeg"],
  videos: ["mp4", "mkv", "mov", "avi", "wmv", "flv", "webm"],
  archives: ["zip", "rar", "7z", "tar", "gz"],
};

// Normaliza extensión (quita query strings por si acaso)
const normalizeExt = (name = "") => {
  const raw = name.split("?")[0].split("#")[0];
  const parts = raw.split(".");
  if (parts.length === 1) return "";
  return parts.pop().toLowerCase();
};

// Normaliza mime (quita parámetros, ejemplo: "application/pdf; charset=bla")
const normalizeMime = (mime = "") => {
  if (!mime) return "";
  return mime.split(";")[0].trim().toLowerCase();
};

/**
 * Devuelve un tipo semántico:
 * "image", "audio", "video", "pdf", "excel", "word", "ppt",
 * "document" (otros documentos), "archive", "unknown"
 */
export function detectFileType(element = {}) {
  const mime = normalizeMime(element?.attachments_data?.type || "");
  const name = element?.attachments_data?.name || "";
  const ext = normalizeExt(name);

  // 1) Si tenemos mime, prioriza
  if (mime) {
    const [major, minor] = mime.split("/"); // ej: ["application","pdf"] o ["image","png"]
    if (major === "image") return "image";
    if (major === "audio") return "audio";
    if (major === "video") return "video";

    // casos application especiales
    if (mime === "application/pdf" || minor === "pdf") return "pdf";

    // ms/office & openxml detection
    if (
      mime.includes("spreadsheet") ||
      mime.includes("excel") ||
      mime.includes("vnd.ms-excel") ||
      mime.includes("vnd.openxmlformats-officedocument.spreadsheetml.sheet")
    ) {
      return "excel";
    }
    if (
      mime.includes("presentation") ||
      mime.includes("powerpoint") ||
      mime.includes("vnd.ms-powerpoint") ||
      mime.includes(
        "vnd.openxmlformats-officedocument.presentationml.presentation"
      )
    ) {
      return "ppt";
    }
    if (
      mime.includes("wordprocessing") ||
      mime.includes("msword") ||
      mime.includes(
        "vnd.openxmlformats-officedocument.wordprocessingml.document"
      )
    ) {
      return "word";
    }

    if (
      major === "text" ||
      mime === "application/json" ||
      mime === "application/xml"
    )
      return "document";

    // archives
    if (
      mime.includes("zip") ||
      mime.includes("compressed") ||
      mime.includes("x-7z") ||
      mime.includes("x-rar")
    ) {
      return "archive";
    }
    // fallback si mime está en formato application/xxxx y contiene extension-like minor
    if (minor && Object.values(formatsAviable).flat().includes(minor)) {
      // si minor es 'vnd.openxmlformats-officedocument.spreadsheetml.sheet' lo tratamos como excel
      if (minor.includes("spreadsheet") || minor.includes("sheet"))
        return "excel";
      if (formatsAviable.images.includes(minor)) return "image";
      if (formatsAviable.audios.includes(minor)) return "audio";
      if (formatsAviable.videos?.includes(minor)) return "video";
    }
  }

  // 2) Si no hay mime fiable, usar ext
  if (ext) {
    if (formatsAviable.images.includes(ext)) return "image";
    if (formatsAviable.audios.includes(ext)) return "audio";
    if (formatsAviable.videos.includes(ext)) return "video";
    if (ext === "pdf") return "pdf";
    if (officeFormats.includes(ext) || ["doc", "docx"].includes(ext)) {
      if (["xls", "xlsx"].includes(ext)) return "excel";
      if (["ppt", "pptx"].includes(ext)) return "ppt";
      if (["doc", "docx", "odt", "rtf"].includes(ext)) return "word";
      return "document";
    }
    if (["txt", "csv"].includes(ext)) return "document";
    if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return "archive";
  }

  // 3) si no se determina, devolver unknown
  return "unknown";
}

/** Mapear key semántica a clave de icono o nombre de preview */
export function getIconKey(fileType) {
  switch (fileType) {
    case "image":
      return "image";
    case "audio":
      return "audio";
    case "video":
      return "video";
    case "pdf":
      return "pdf";
    case "excel":
      return "excel";
    case "word":
      return "word";
    case "ppt":
      return "ppt";
    case "archive":
      return "archive";
    case "document":
      return "document";
    default:
      return "file";
  }
}
